import { useConfirmation } from "shared/components/ConfirmationProvider";
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Autocomplete,
  alpha,
  Chip,
  Paper,
  InputAdornment,
  Typography,
} from "@mui/material";
import {
  AddBusiness,
  WhatsApp,
  CheckCircle,
  Error as ErrorIcon,
  Save,
  Close,
  Refresh,
  Group,
  Work,
  Computer,
  Storefront,
} from "@mui/icons-material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import PropTypes from "prop-types";
import { getCommerceWhatsAppTerminalsService } from "modules/jarvis/services/whatsappService";

const CreateWhatsappCopDialog = ({
  open,
  onClose,
  onCreate,
  attributes,
  availableComercios,
  loading = false,
}) => {
  const confirm = useConfirmation();

  const [formData, setFormData] = useState({
    idcommerce: "",
    idterminal: "",
    namecommerce: "",
    terminalname: "",
    groupnoc: "",
    groupName: "",
    chatId: "",
    nameworkflows: "",
    status: true,
    pulse: false,
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [selectedComercio, setSelectedComercio] = useState(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [selectedGrupoWP, setSelectedGrupoWP] = useState(null);
  const [terminales, setTerminales] = useState([]);
  const [loadingTerminales, setLoadingTerminales] = useState(false);
  const [comercioInputValue, setComercioInputValue] = useState("");
  const [grupoWPInputValue, setGrupoWPInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");

  // Determinar si el workflow seleccionado es EXACTAMENTE J.A.R.V.I.S INTEGRACION
  // CORREGIDO: Solo los workflows que contengan "INTEGRACION" en su nombre
  const isJarvisIntegration =
    formData.nameworkflows &&
    (formData.nameworkflows.includes("INTEGRACION") ||
      formData.nameworkflows.includes("INTEGRACIÓN"));

  // Opciones para el Autocomplete de grupos WhatsApp
  const grupoWPOptions = attributes?.group_wp_dis || [];

  // Resetear formulario cuando se abre/cierra
  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  // Cargar terminales cuando se selecciona un comercio
  useEffect(() => {
    const cargarTerminales = async () => {
      if (selectedComercio && selectedComercio.id) {
        setLoadingTerminales(true);
        try {
          const result = await getCommerceWhatsAppTerminalsService(selectedComercio.id);
          if (result.success) {
            setTerminales(result.data);
          } else {
            console.error("Error al cargar terminales:", result.error);
          }
        } catch (error) {
          console.error("Error al cargar terminales:", error);
        } finally {
          setLoadingTerminales(false);
        }
      } else {
        setTerminales([]);
        setSelectedTerminal(null);
        setFormData((prev) => ({
          ...prev,
          idterminal: "",
          terminalname: "",
        }));
      }
    };

    cargarTerminales();
  }, [selectedComercio]);

  const resetForm = () => {
    setFormData({
      idcommerce: "",
      idterminal: "",
      namecommerce: "",
      terminalname: "",
      groupnoc: "",
      groupName: "",
      chatId: "",
      nameworkflows: "",
      status: true,
      pulse: false,
    });
    setErrors({});
    setTouched({});
    setSelectedComercio(null);
    setSelectedTerminal(null);
    setSelectedGrupoWP(null);
    setTerminales([]);
    setComercioInputValue("");
    setGrupoWPInputValue("");
    setIsSubmitting(false);
    setSubmitStatus(null);
    setStatusMessage("");
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));

    validateField(field, value);
  };

  const handleSwitchChange = (field) => (event) => {
    const checked = event.target.checked;
    setFormData((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const handleComercioSelect = (event, newValue) => {
    setSelectedComercio(newValue);
    setSelectedTerminal(null);
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        idcommerce: newValue.id.toString(),
        namecommerce: newValue.nombre,
        idterminal: "",
        terminalname: "",
      }));
      validateField("idcommerce", newValue.id.toString());
      validateField("namecommerce", newValue.nombre);
    } else {
      setFormData((prev) => ({
        ...prev,
        idcommerce: "",
        namecommerce: "",
        idterminal: "",
        terminalname: "",
      }));
    }
  };

  const handleTerminalSelect = (event) => {
    const terminalId = event.target.value;

    if (terminalId === "") {
      // Opción "Sin terminal" seleccionada
      setFormData((prev) => ({
        ...prev,
        idterminal: "",
        terminalname: "",
      }));
      setSelectedTerminal(null);
    } else {
      const terminalSeleccionada = terminales.find((t) => t.id.toString() === terminalId);
      setFormData((prev) => ({
        ...prev,
        idterminal: terminalId,
        terminalname: terminalSeleccionada?.nombre || "",
      }));
      setSelectedTerminal(terminalSeleccionada || null);
    }

    validateField("idterminal", terminalId);
  };

  const handleGrupoWPSelect = (event, newValue) => {
    setSelectedGrupoWP(newValue);
    if (newValue) {
      setFormData((prev) => ({
        ...prev,
        groupName: newValue.groupName,
        chatId: newValue.chatId,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        groupName: "",
        chatId: "",
      }));
    }
  };

  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "idcommerce":
        if (!value) error = "Debe seleccionar un comercio de la lista";
        break;
      case "namecommerce":
        if (!value) error = "El nombre del comercio es requerido";
        break;
      case "idterminal":
        // CORREGIDO: Solo validar terminal si NO es J.A.R.V.I.S INTEGRACION
        // Ahora usa la nueva condición que solo detecta workflows con "INTEGRACION"
        if (!isJarvisIntegration && !value) error = "Debe seleccionar una terminal";
        break;
      case "groupnoc":
        if (!value) error = "El grupo NOC es requerido";
        break;
      case "nameworkflows":
        if (!value) error = "El workflow es requerido";
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [field]: error,
    }));

    return !error;
  };

  const validateForm = () => {
    const fieldsToValidate = ["idcommerce", "namecommerce", "groupnoc", "nameworkflows"];

    // CORREGIDO: Agregar idterminal a la validación solo si NO es J.A.R.V.I.S INTEGRACION
    if (!isJarvisIntegration) {
      fieldsToValidate.push("idterminal");
    }

    let isValid = true;

    fieldsToValidate.forEach((field) => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) isValid = false;
      setTouched((prev) => ({
        ...prev,
        [field]: true,
      }));
    });

    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitStatus("error");
      setStatusMessage("Por favor, complete todos los campos requeridos correctamente.");
      return;
    }

    // Preparar datos asegurando booleanos estrictos
    const datosAEnviar = {
      status: formData.status === true,
      idcommerce: parseInt(formData.idcommerce, 10),
      // CORREGIDO: Si es J.A.R.V.I.S INTEGRACION y no hay terminal, enviar null
      idterminal:
        isJarvisIntegration && !formData.idterminal ? null : parseInt(formData.idterminal, 10),
      terminalname: formData.terminalname || "",
      namecommerce: formData.namecommerce,
      groupnoc: formData.groupnoc,
      groupName: formData.groupName || "",
      chatId: formData.chatId || "",
      nameworkflows: formData.nameworkflows,
      pulse: formData.pulse === true,
    };

    setIsSubmitting(true);
    setSubmitStatus(null);
    setStatusMessage("Creando configuración...");

    try {
      const result = await onCreate(datosAEnviar);

      if (result && result.success) {
        setStatusMessage(result.message || "Configuración creada exitosamente.");
        setSubmitStatus("success");

        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(result?.error || "Error al crear la configuración");
      }
    } catch (error) {
      console.error("Error al crear configuración:", error);
      setSubmitStatus("error");
      setStatusMessage(error.message || "Error desconocido al crear la configuración.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = async () => {
    if (isSubmitting) {
      if (await confirm("La creación está en progreso. ¿Desea cancelar?")) {
        setIsSubmitting(false);
        onClose();
      }
    } else {
      onClose();
    }
  };

  const isFieldError = (field) => {
    return touched[field] && errors[field];
  };

  const renderForm = () => (
    <Stack spacing={3}>
      {/* Selección de comercio existente - OBLIGATORIO */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "#075e54",
          backgroundColor: alpha("#075e54", 0.03),
        }}
      >
        <MDTypography
          variant="subtitle1"
          color="success"
          fontWeight="600"
          mb={2}
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ color: "#075e54" }}
        >
          <Storefront fontSize="small" />
          SELECCIONAR COMERCIO * (OBLIGATORIO)
        </MDTypography>
        <Autocomplete
          options={availableComercios}
          getOptionLabel={(option) => option.nombre}
          value={selectedComercio}
          onChange={handleComercioSelect}
          inputValue={comercioInputValue}
          onInputChange={(event, newInputValue) => setComercioInputValue(newInputValue)}
          loading={loading}
          loadingText="Cargando comercios..."
          noOptionsText="No se encontraron comercios"
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Buscar y seleccionar comercio por nombre..."
              variant="outlined"
              fullWidth
              error={isFieldError("idcommerce") || isFieldError("namecommerce")}
              helperText={
                (isFieldError("idcommerce") || isFieldError("namecommerce")) &&
                "Debe seleccionar un comercio de la lista"
              }
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <Storefront sx={{ color: "#075e54" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem {...props} sx={{ py: 2, px: 2.5 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <Typography variant="body1" sx={{ fontSize: "0.95rem", fontWeight: 500 }}>
                  {option.nombre}
                </Typography>
                <Chip
                  label={`ID: ${option.id}`}
                  size="small"
                  sx={{
                    fontSize: "0.75rem",
                    backgroundColor: alpha("#075e54", 0.1),
                    color: "#075e54",
                    fontWeight: 600,
                  }}
                />
              </Box>
            </MenuItem>
          )}
        />
      </Paper>

      {/* Formulario principal */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Grid container spacing={3}>
          {/* ID Comercio - Solo lectura */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID Comercio *"
              value={formData.idcommerce}
              disabled
              variant="outlined"
              error={isFieldError("idcommerce")}
              helperText={isFieldError("idcommerce") ? errors.idcommerce : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsApp sx={{ color: isFieldError("idcommerce") ? "#f44336" : "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Nombre Comercio - Solo lectura */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nombre Comercio *"
              value={formData.namecommerce}
              disabled
              variant="outlined"
              error={isFieldError("namecommerce")}
              helperText={isFieldError("namecommerce") ? errors.namecommerce : ""}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Storefront
                      sx={{ color: isFieldError("namecommerce") ? "#f44336" : "#9e9e9e" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Workflow - AHORA PRIMERO */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("nameworkflows")}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.95rem",
                },
              }}
            >
              <InputLabel sx={{ fontSize: "0.95rem" }} error={isFieldError("nameworkflows")}>
                Workflow *
              </InputLabel>
              <Select
                value={formData.nameworkflows}
                onChange={handleChange("nameworkflows")}
                label="Workflow *"
                error={isFieldError("nameworkflows")}
                disabled={isSubmitting}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Work sx={{ color: isFieldError("nameworkflows") ? "#f44336" : "#075e54" }} />
                  </InputAdornment>
                }
              >
                {attributes?.workflows?.map((workflow) => (
                  <MenuItem key={workflow.nameworkflows} value={workflow.nameworkflows}>
                    {workflow.nameworkflows}
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("nameworkflows") && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.nameworkflows}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Grupo NOC */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("groupnoc")}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.95rem",
                },
              }}
            >
              <InputLabel sx={{ fontSize: "0.95rem" }} error={isFieldError("groupnoc")}>
                Grupo NOC *
              </InputLabel>
              <Select
                value={formData.groupnoc}
                onChange={handleChange("groupnoc")}
                label="Grupo NOC *"
                error={isFieldError("groupnoc")}
                disabled={isSubmitting}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Group sx={{ color: isFieldError("groupnoc") ? "#f44336" : "#075e54" }} />
                  </InputAdornment>
                }
              >
                {attributes?.groupnoc?.map((grupo) => (
                  <MenuItem key={grupo} value={grupo}>
                    {grupo}
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("groupnoc") && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.groupnoc}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Terminal - Selector MODIFICADO: opcional solo para workflows que contengan "INTEGRACION" */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              error={isFieldError("idterminal")}
              disabled={!selectedComercio || loadingTerminales}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                },
              }}
            >
              <InputLabel error={isFieldError("idterminal")}>
                Terminal {isJarvisIntegration ? "(Opcional)" : "*"}
              </InputLabel>
              <Select
                value={formData.idterminal}
                onChange={handleTerminalSelect}
                label={`Terminal ${isJarvisIntegration ? "(Opcional)" : "*"}`}
                error={isFieldError("idterminal")}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <Computer sx={{ color: isFieldError("idterminal") ? "#f44336" : "#075e54" }} />
                  </InputAdornment>
                }
              >
                {/* CORREGIDO: Solo mostrar "Sin terminal" para workflows de INTEGRACION */}
                {isJarvisIntegration && (
                  <MenuItem value="">
                    <em>Sin terminal (opcional)</em>
                  </MenuItem>
                )}
                {terminales.map((terminal) => (
                  <MenuItem key={terminal.id} value={terminal.id.toString()}>
                    {terminal.nombre}
                  </MenuItem>
                ))}
              </Select>
              {isFieldError("idterminal") && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.idterminal}
                </Typography>
              )}
              {/* CORREGIDO: Mensaje solo para workflows de INTEGRACION */}
              {isJarvisIntegration && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                  Para workflows de INTEGRACION la terminal es opcional
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Terminal Name - Solo lectura (auto-completado) */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Terminal Name"
              value={formData.terminalname}
              disabled
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Computer sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            />
          </Grid>

          {/* Grupo WhatsApp - OPCIONAL */}
          <Grid item xs={12}>
            <MDTypography variant="subtitle2" color="text.secondary" fontWeight="500" mb={1}>
              GRUPO WHATSAPP
            </MDTypography>
            <Autocomplete
              options={grupoWPOptions}
              getOptionLabel={(option) => option.groupName}
              value={selectedGrupoWP}
              onChange={handleGrupoWPSelect}
              inputValue={grupoWPInputValue}
              onInputChange={(event, newInputValue) => setGrupoWPInputValue(newInputValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar y seleccionar grupo WhatsApp..."
                  variant="outlined"
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <WhatsApp sx={{ color: "#075e54" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiInputBase-root": {
                      height: "56px",
                      fontSize: "1rem",
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <MenuItem {...props} sx={{ py: 2, px: 2.5 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Typography variant="body1" sx={{ fontSize: "0.95rem", fontWeight: 500 }}>
                      {option.groupName}
                    </Typography>
                    <Chip
                      label="Chat ID"
                      size="small"
                      sx={{
                        fontSize: "0.65rem",
                        backgroundColor: alpha("#075e54", 0.1),
                        color: "#075e54",
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </MenuItem>
              )}
            />
          </Grid>

          {/* Chat ID (auto-completado) - OPCIONAL */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Chat ID (opcional)"
              value={formData.chatId}
              onChange={handleChange("chatId")}
              disabled={isSubmitting || !!selectedGrupoWP}
              variant="outlined"
              placeholder={selectedGrupoWP ? "Auto-completado" : "Ej: 120363425759294959@g.us"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsApp sx={{ color: selectedGrupoWP ? "#9e9e9e" : "#075e54" }} />
                  </InputAdornment>
                ),
                readOnly: !!selectedGrupoWP,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: "56px",
                  fontSize: "1rem",
                },
                "& .MuiInputBase-root.Mui-disabled": {
                  backgroundColor: selectedGrupoWP ? "#f5f5f5" : "transparent",
                },
              }}
            />
          </Grid>

          {/* Switches */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: alpha("#075e54", 0.02),
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.status}
                        onChange={handleSwitchChange("status")}
                        color="success"
                        disabled={isSubmitting}
                        size="medium"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ fontSize: "0.95rem" }}>
                          Estado
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.status ? "Activo" : "Inactivo"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="end"
                    sx={{ margin: 0, width: "100%" }}
                  />
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: "8px",
                    border: "1px solid",
                    borderColor: "divider",
                    backgroundColor: alpha("#075e54", 0.02),
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.pulse}
                        onChange={handleSwitchChange("pulse")}
                        color="info"
                        disabled={isSubmitting}
                        size="medium"
                      />
                    }
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="body1" fontWeight="600" sx={{ fontSize: "0.95rem" }}>
                          Pulso
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.pulse ? "Activo" : "Inactivo"}
                        </Typography>
                      </Box>
                    }
                    labelPlacement="end"
                    sx={{ margin: 0, width: "100%" }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Resumen de configuración */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: "12px",
          border: "1px dashed",
          borderColor: alpha("#075e54", 0.3),
          backgroundColor: alpha("#075e54", 0.02),
        }}
      >
        <Typography variant="subtitle2" fontWeight="600" gutterBottom>
          RESUMEN DE CONFIGURACIÓN
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} md={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Comercio:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.namecommerce || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Terminal:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.terminalname || (isJarvisIntegration ? "Opcional" : "—")}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Grupo NOC:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.groupnoc || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Workflow:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.nameworkflows || "—"}
            </Typography>
          </Grid>
          <Grid item xs={6} md={2.4}>
            <Typography variant="caption" color="text.secondary" display="block">
              Grupo WP:
            </Typography>
            <Typography variant="body2" fontWeight="600" noWrap>
              {formData.groupName || "No seleccionado"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Stack>
  );

  const renderSuccessContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CheckCircle sx={{ fontSize: 80, color: "success.main", mb: 3 }} />
      <Typography variant="h6" fontWeight="bold" color="success" gutterBottom>
        Configuración Creada Exitosamente
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {statusMessage}
      </Typography>
    </Box>
  );

  const renderErrorContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <ErrorIcon sx={{ fontSize: 80, color: "error.main", mb: 3 }} />
      <Typography variant="h6" fontWeight="bold" color="error" gutterBottom>
        Error al Crear Configuración
      </Typography>
      <Alert severity="error" sx={{ mb: 3 }}>
        <Typography variant="body2">{statusMessage}</Typography>
      </Alert>
    </Box>
  );

  const renderLoadingContent = () => (
    <Box sx={{ textAlign: "center", py: 4 }}>
      <CircularProgress size={80} thickness={4} sx={{ color: "#075e54", mb: 3 }} />
      <Typography variant="h6" fontWeight="medium" color="#075e54" gutterBottom>
        Creando Configuración
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {statusMessage}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "16px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          pb: 2,
          pt: 3,
          px: 3,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center">
          {isSubmitting ? (
            <CircularProgress size={28} sx={{ mr: 2, color: "#075e54" }} />
          ) : submitStatus === "success" ? (
            <CheckCircle sx={{ mr: 2, fontSize: "1.75rem", color: "success.main" }} />
          ) : submitStatus === "error" ? (
            <ErrorIcon sx={{ mr: 2, fontSize: "1.75rem", color: "error.main" }} />
          ) : (
            <AddBusiness sx={{ mr: 2, fontSize: "1.75rem", color: "#075e54" }} />
          )}
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Crear Configuración WhatsApp
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Complete todos los campos requeridos para continuar
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, px: 3 }}>
        <Stack spacing={3}>
          {(isSubmitting || submitStatus) && submitStatus !== "success" && (
            <Alert
              severity={isSubmitting ? "info" : submitStatus === "success" ? "success" : "error"}
              icon={isSubmitting ? <CircularProgress size={20} /> : undefined}
              sx={{ borderRadius: "8px" }}
            >
              <Typography variant="body2" fontWeight="medium">
                {statusMessage}
              </Typography>
            </Alert>
          )}

          {isSubmitting && renderLoadingContent()}
          {submitStatus === "success" && renderSuccessContent()}
          {submitStatus === "error" && renderErrorContent()}
          {!isSubmitting && !submitStatus && renderForm()}
        </Stack>
      </DialogContent>

      {/* Footer */}
      {!isSubmitting && !submitStatus && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cancelar
            </MDButton>
            <MDButton
              onClick={handleSubmit}
              variant="contained"
              sx={{
                px: 4,
                minWidth: 200,
                borderRadius: "8px",
                fontWeight: "bold",
                height: "48px",
                backgroundColor: "#075e54",
                "&:hover": { backgroundColor: "#128c7e" },
              }}
              startIcon={<Save />}
              disabled={
                isSubmitting ||
                !selectedComercio ||
                !formData.groupnoc ||
                !formData.nameworkflows ||
                // CORREGIDO: Solo requerir terminal si NO es workflow de INTEGRACION
                (!isJarvisIntegration && !formData.idterminal)
              }
            >
              CREAR CONFIGURACIÓN
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "error" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%" gap={2}>
            <MDButton
              onClick={handleClose}
              variant="outlined"
              color="secondary"
              startIcon={<Close />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Cerrar
            </MDButton>
            <MDButton
              onClick={() => {
                setSubmitStatus(null);
                setStatusMessage("");
              }}
              variant="contained"
              color="warning"
              startIcon={<Refresh />}
              sx={{ px: 3, minWidth: 120, borderRadius: "8px", height: "48px" }}
            >
              Reintentar
            </MDButton>
          </Box>
        </DialogActions>
      )}

      {submitStatus === "success" && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
          <Box display="flex" justifyContent="flex-end" width="100%">
            <MDButton
              onClick={handleClose}
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              sx={{ px: 4, minWidth: 150, borderRadius: "8px", height: "48px" }}
            >
              Aceptar
            </MDButton>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

CreateWhatsappCopDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  attributes: PropTypes.object,
  availableComercios: PropTypes.array,
  loading: PropTypes.bool,
};

CreateWhatsappCopDialog.defaultProps = {
  attributes: {
    groupnoc: [],
    workflows: [],
    group_wp_dis: [],
  },
  availableComercios: [],
  loading: false,
};

export default React.memo(CreateWhatsappCopDialog);
